import { useMutation, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Link,
  Spinner,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

import { DatadogConfigModal } from './datadogConfigModal/DatadogConfigModal';
import {
  datadogConfigQuery,
  disableDatadogForwardingLegacyMetricNamesMutation,
} from './graphql';

interface Props {
  serviceId: string;
}

export const DatadogIntegration = ({ serviceId }: Props) => {
  const {
    data,
    loading,
    error: queryError,
  } = useQuery<
    GraphQLTypes.DatadogConfigQuery,
    GraphQLTypes.DatadogConfigQueryVariables
  >(datadogConfigQuery, {
    variables: { serviceId },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [error, setError] = useState<string>();
  const [disableDatadogForwardingLegacyMetricNames] = useMutation<
    GraphQLTypes.DisableDatadogForwardingLegacyMetricNames,
    GraphQLTypes.DisableDatadogForwardingLegacyMetricNamesVariables
  >(disableDatadogForwardingLegacyMetricNamesMutation);

  const currentlyEnabled =
    data?.service?.datadogMetricsConfig?.enabled ?? false;
  const currentApiKey = data?.service?.datadogMetricsConfig?.apiKey;
  const currentApiRegion = data?.service?.datadogMetricsConfig?.apiRegion;
  const legacyMetricNames =
    currentlyEnabled && data?.service?.datadogMetricsConfig?.legacyMetricNames;

  const onCancel = useCallback(() => {
    setModalVisible(false);
    setError(undefined);
  }, [setModalVisible, setError]);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            Datadog forwarding
            <Tooltip
              interactive
              label={
                <>
                  <Link isExternal to={DocsRouter.path('DatadogIntegration')}>
                    Visit the docs
                  </Link>{' '}
                  to learn more about integrating with Datadog.
                </>
              }
            >
              <IconInfo className="text-icon-primary" />
            </Tooltip>
          </CardTitle>
          <PermissionGuard
            graphId={data?.service?.id}
            graphPermissions="canQueryIntegrations"
          >
            <CardDescription>
              Forward a copy of all performance metrics for{' '}
              <em>{data?.service?.title ?? '--'}</em> to Datadog.{' '}
              {currentApiKey && (
                <>
                  Datadog integration is currently{' '}
                  <span
                    className={classnames(
                      'font-semibold',
                      currentlyEnabled ? 'text-success' : 'text-error',
                    )}
                  >
                    {currentlyEnabled ? 'enabled' : 'disabled'}.
                  </span>
                </>
              )}
            </CardDescription>
          </PermissionGuard>
          {loading ? (
            <CardHeaderControl>
              <Spinner size="xs" data-testid="loading-spinner" />
            </CardHeaderControl>
          ) : (
            <PermissionGuard
              graphId={data?.service?.id}
              graphPermissions="canQueryIntegrations"
              fallback={null}
            >
              <ButtonGroup>
                <PermissionWarning
                  graphId={data?.service?.id}
                  graphPermissions="canManageIntegrations"
                  childRenderer={(hasPermission) => (
                    <Button
                      isDisabled={!hasPermission}
                      variant="secondary"
                      onClick={() => setModalVisible(true)}
                    >
                      Configure
                    </Button>
                  )}
                />
                {legacyMetricNames && (
                  <PermissionWarning
                    graphId={data?.service?.id}
                    graphPermissions="canManageIntegrations"
                    childRenderer={(hasPermission) => (
                      <Button
                        isDisabled={!hasPermission}
                        variant="primary"
                        onClick={() =>
                          disableDatadogForwardingLegacyMetricNames({
                            variables: { serviceId },
                          })
                        }
                      >
                        Transition to modern mode
                      </Button>
                    )}
                  />
                )}
              </ButtonGroup>
            </PermissionGuard>
          )}
        </CardHeader>
        {(error || queryError || legacyMetricNames) && (
          <CardBody>
            {legacyMetricNames && (
              <Alert status="info">
                Your Datadog integration is currently running in legacy mode.
                This means that its Datadog metric names start with{' '}
                <code>apollo.engine.operations</code> instead of{' '}
                <code>apollo.operations</code>, and the metrics are tagged with{' '}
                <code>service:{serviceId}</code> instead of{' '}
                <code>graph:{serviceId}</code>. You can transition to modern
                mode when you are ready to update your Datadog dashboards and
                monitors to use the newer names.{' '}
                <Link isExternal to={DocsRouter.path('DatadogIntegration')}>
                  See the docs
                </Link>{' '}
                for more details.
              </Alert>
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {queryError && <ErrorMessage error={queryError} />}
          </CardBody>
        )}
      </Card>
      <DatadogConfigModal
        isOpen={modalVisible}
        serviceId={serviceId}
        apiKey={currentApiKey}
        apiRegion={currentApiRegion}
        currentlyEnabled={currentlyEnabled}
        onClose={onCancel}
      />
    </>
  );
};
